import { useCallback, useEffect, useRef } from 'react';

/**
 * Short tones (and a vibration where supported) so an operator hears
 * whether a scan was accepted without looking at the screen. The audio
 * context is created on first use and closed on unmount.
 */
export function useScanFeedback() {
  const context = useRef<AudioContext | null>(null);

  useEffect(
    () => () => {
      void context.current?.close();
      context.current = null;
    },
    [],
  );

  const tone = useCallback((frequency: number, duration: number) => {
    try {
      if (typeof AudioContext === 'undefined') return;
      context.current ??= new AudioContext();
      const ctx = context.current;
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.frequency.value = frequency;
      gain.gain.value = 0.08;
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start();
      oscillator.stop(ctx.currentTime + duration);
    } catch {
      // Sound is a convenience; the on-screen message is authoritative.
    }
  }, []);

  return {
    accepted: useCallback(() => {
      tone(880, 0.09);
      navigator.vibrate?.(40);
    }, [tone]),
    rejected: useCallback(() => {
      tone(220, 0.25);
      navigator.vibrate?.([80, 60, 80]);
    }, [tone]),
  };
}
